import { IGenericJSON } from '../helpers/interface';
import { School } from './school';

const PRIORITY_MAP: Record<string, ReportPriority> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

export type ReportStatus = 'Submitted' | 'Acknowledged' | 'In Progress' | 'Resolved';

const STATUS_MAP: Record<string, ReportStatus> = {
  SUBMITTED: 'Submitted',
  ACKNOWLEDGED: 'Acknowledged',
  INPROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
};

export function mapStatus(raw: unknown): ReportStatus {
  if (raw === null || raw === undefined) {
    return 'Submitted';
  }
  const key = String(raw)
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
  return STATUS_MAP[key] ?? 'Submitted';
}
export type ReportCategory = 'Bug' | 'Outage' | 'Feature Request' | 'Access Issue' | 'Other';
export type ReportPriority = 'Low' | 'Medium' | 'High' | 'Urgent';

export interface AssignableUser {
  id: number;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  /** Saved avatar image URL, null if the user has never saved one. */
  profile_picture_path: string | null;
}

export function assignableUserLabel(user: AssignableUser): string {
  const first = user.preferred_name || user.first_name || '';
  return `${first} ${user.last_name || ''}`.trim() || `User #${user.id}`;
}

export class ReportResponse {
  author: string;
  role: 'admin' | 'user';
  date: string;
  text: string;

  constructor(data: IGenericJSON) {
    this.author = data['author'];
    this.role = data['role'];
    this.date = data['date'];
    this.text = data['text'];
  }

  public static digest(data: IGenericJSON): IssueReport {
    return new IssueReport(data);
  }

  public static digestResponse(data: IGenericJSON[]): IssueReport[] {
    return data.map((r: IGenericJSON) => this.digest(r));
  }
}

export class ReportMedia {
  id: number;
  filename: string;
  path: string;

  constructor(data: IGenericJSON) {
    this.id = data['id'];
    this.filename = data['filename'];
    this.path = data['path'];
  }

  public static digest(data: IGenericJSON): ReportMedia {
    return new ReportMedia(data);
  }
}

export class IssueReport {
  id: number;
  title: string;
  category: ReportCategory;
  priority: ReportPriority;
  school: School | null;
  status: ReportStatus;
  submittedBy: number;
  submittedDate: Date;
  assignedTo: number | null;
  assigneeName: string | null;
  description: string;
  responses: ReportResponse[];
  media: ReportMedia[];

  constructor(data: IGenericJSON) {
    this.id = Number(data['id']);
    this.title = data['title'];
    this.category = data['category'];
    this.priority = PRIORITY_MAP[data['priority']];
    this.status = mapStatus(data['status']);
    this.school = data['School'] ? School.digest(data['School']) : null;
    this.submittedBy = data['submitted_by'];
    this.submittedDate = new Date(data['created_at']);

    const assignee = data['assignee'];
    this.assignedTo = data['assigned_to'] ?? assignee?.id ?? null;
    this.assigneeName = assignee
      ? `${assignee['preferred_name'] || assignee['first_name'] || ''} ${
          assignee['last_name'] || ''
        }`.trim() || null
      : null;

    this.description = data['description'];

    this.responses = data['responses']
      ? data['responses'].map((r: IGenericJSON) => ReportResponse.digest(r))
      : [];

    this.media = data['media'] ? data['media'].map((m: IGenericJSON) => ReportMedia.digest(m)) : [];
  }

  public static digest(data: IGenericJSON): IssueReport {
    return new IssueReport(data);
  }

  public static digestResponse(data: IGenericJSON[]): IssueReport[] {
    return data.map((r: IGenericJSON) => this.digest(r));
  }
}

export const STATUS_ORDER: ReportStatus[] = [
  'Submitted',
  'Acknowledged',
  'In Progress',
  'Resolved',
];

export const STATUS_TO_ENUM: Record<ReportStatus, string> = {
  Submitted: 'SUBMITTED',
  Acknowledged: 'ACKNOWLEDGED',
  'In Progress': 'INPROGRESS',
  Resolved: 'RESOLVED',
};
